import { useIntl } from 'react-intl'
import '../styles/index.scss'
import { ToolTipUI } from '../../widgets/components/UI/ToolTipUI'
import { BasicButton } from '../../widgets/components/UI/BasicButton'
import '../../data-rooms/styles/DataRooms.scss'
import { useAuth } from '../../auth'
import { Dispatch, SetStateAction, useState } from 'react'
import { OtherSettingsInputType } from '../core/_models'
import { toast } from 'react-toastify'
import { OtherSettingsCompany } from '../core/_constants'
import { updateCompanyOtherSettings } from '../core/_requests'
import Skeleton from 'react-loading-skeleton'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'

export const OtherCompanySettings = ({
  getCompanyApiLoading,
  showDemoDashboard,
  setshowDemoDashboard,
}: {
  getCompanyApiLoading: boolean
  showDemoDashboard: boolean
  setshowDemoDashboard: Dispatch<SetStateAction<boolean>>
}) => {
  const { formatMessage } = useIntl()
  const [loading, setLoading] = useState(false)
  const { companyId, currentUser } = useAuth()

  const handleToogle = (checked: boolean) => {
    setshowDemoDashboard(checked)
  }

  const handleSumbit = async () => {
    try {
      setLoading(true)
      if (companyId) {
        const {
          data: { success, errors },
        } = await updateCompanyOtherSettings(showDemoDashboard, companyId)
        if (success) {
          setLoading(false)
          toast.success(formatMessage({ id: 'settings updated successfully' }))
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.UpdateCompanyOtherSettingsSuccessful,
              action: 'normal',
              metadata: {
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                setting_show_demo_dashboard: showDemoDashboard,
              },
            },
          })
        } else {
          setLoading(false)
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.UpdateCompanyOtherSettingsFailed,
              action: 'normal',
              metadata: {
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                setting_show_demo_dashboard: showDemoDashboard,
                error: errors[0],
              },
            },
          })
        }
      }
    } catch (err) {
      setLoading(false)
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.UpdateCompanyOtherSettingsFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            setting_show_demo_dashboard: showDemoDashboard,
            error: err,
          },
        },
      })
    }
  }

  const OtherSettingsCompanyInput = [
    {
      id: OtherSettingsCompany.DUMMY_DASHBOARD_DATA,
      title: formatMessage({ id: 'Dummy Data in Dashboard' }),
      subtitle: formatMessage({
        id: 'Switching this OFF will replace sample/demo data with live information across all dashboards, providing an accurate view of your recordkeeping activities.',
      }),
      defaultValue: showDemoDashboard,
      toggleLabel: 'dummyDashboardData',
    },
  ]

  return (
    <div className='company-container p-6'>
      {getCompanyApiLoading ? (
        <>
          <div className='w-100'>
            <div className='mb-10'>
              <Skeleton
                width={250}
                height={30}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
            {Array(6)
              .fill(null)
              .map((_, index) => (
                <>
                  <div className='d-flex gap-4 mt-8' key={index}>
                    <div>
                      <Skeleton
                        width={50}
                        height={30}
                        baseColor='var(--kt-skeleton-bg-color)'
                        highlightColor='var(--kt-skeleton-highlight-bg-color)'
                      />{' '}
                    </div>
                    <div className='w-100'>
                      <div className='w-md-25 w-50'>
                        <Skeleton
                          height={20}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />{' '}
                      </div>
                      <div className='mt-2'>
                        <Skeleton
                          height={15}
                          baseColor='var(--kt-skeleton-bg-color)'
                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                        />{' '}
                      </div>
                    </div>
                  </div>
                </>
              ))}
            <div className='w-100 d-md-flex justify-content-md-end gap-4 align-items-md-end h-md-200px'>
              <div className='w-md-25 me-md-20 mt-8'>
                <Skeleton
                  height={40}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className='fs-6 fw-semibold pb-5'>{formatMessage({ id: 'Other Settings' })}</div>

          {OtherSettingsCompanyInput.map(
            ({ id, title, subtitle, defaultValue }: OtherSettingsInputType, index: number) => (
              <div className='border-bottom' key={id}>
                <form className='form-check form-check-custom form-check-solid form-check-primary form-switch pb-3 pt-3'>
                  <label className='togglebtn'>
                    <input
                      className='togglebtn__input'
                      name='model.app.sidebar.default.minimize.desktop.hoverable'
                      defaultChecked={defaultValue}
                      id={id.toString()}
                      type='checkbox'
                      onChange={(e) => {
                        handleToogle(e.target.checked)
                      }}
                    />
                    <div className='togglebtn__fill'></div>
                  </label>
                  <div className='d-flex flex-column justify-content-end mt-2'>
                    <div className='d-flex'>
                      <div>
                        <label
                          className='form-check-label fw-semibold font-size-13 title-notification'
                          data-bs-toggle='tooltip'
                          data-kt-initialized='1'
                          htmlFor={id.toString()}
                        >
                          {formatMessage({ id: title })}
                        </label>
                      </div>
                      <div>
                        {
                          <ToolTipUI
                            tooltipText={formatMessage({
                              id: 'Turn this switch “Off” if you don’t want to see any dummy data in your dashboard. Turn it “ON” to see dummy data.',
                            })}
                          />
                        }
                      </div>
                    </div>
                    <label
                      className='form-check-label text-clr58 fw-normal fs-7'
                      data-bs-toggle='tooltip'
                      data-kt-initialized='1'
                      htmlFor={id.toString()}
                    >
                      {formatMessage({ id: subtitle })}
                    </label>
                  </div>
                </form>
              </div>
            )
          )}

          <div className='d-md-flex justify-content-md-end w-md-auto w-100 button-margin pb-md-8 me-md-8 me-0 pt-4 pt-md-auto'>
            <BasicButton
              buttonText={formatMessage({ id: 'Save Changes' })}
              onClick={handleSumbit}
              disabled={loading}
              loading={loading}
              height='44px'
              color='#4776E6'
              textColor='white'
              padding='12px 24px'
              customClass='w-md-auto w-100'
            />
          </div>
        </>
      )}
    </div>
  )
}
